"use client"

import { zodResolver } from "@hookform/resolvers/zod";
import { ChampignonsCreateSchema, ChampignonsFormData } from "@repo/types";
import { imageService } from "@repo/ui/lib/services/index";
import {
  compressImageForUpload,
  formatFileSize,
} from "@repo/ui/lib/utils/image-compression.util";
import { validateImageFile, resolveUploadErrorMessage } from "@repo/ui/lib/utils/image-upload.util";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useChampignon, useCreateChampignon, useUpdateChampignon, useUploadChampignonImage } from "../../../hooks/api/champignons";

interface UseChampignonFormProps {
  id?: string;
  champignonsId?: number;
  onSuccess: () => void;
}

function isBlobPreviewUrl(url: string | null): boolean {
  return Boolean(url?.startsWith('blob:'));
}

export function useChampignonForm({ id, champignonsId, onSuccess }: UseChampignonFormProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isOptimizingImage, setIsOptimizingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [compressionMessage, setCompressionMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingUploadId, setPendingUploadId] = useState<number | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const effectiveChampignonId = id ? parseInt(id, 10) : champignonsId;

  const { data: champignon, isLoading } = useChampignon(effectiveChampignonId || 0);
  const createChampignon = useCreateChampignon();
  const updateChampignon = useUpdateChampignon();
  const uploadImage = useUploadChampignonImage();
  const isEditing = !!effectiveChampignonId;

  const form = useForm<ChampignonsFormData>({
    //@ts-ignore
    resolver: zodResolver(ChampignonsCreateSchema),
    defaultValues: {
      name: '',
      attaque_f: '',
      attaque_r: '',
      category: 'F',
    },
  });

  const setPreview = useCallback((url: string | null) => {
    if (previewUrlRef.current && isBlobPreviewUrl(previewUrlRef.current)) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    previewUrlRef.current = url;
    setPreviewUrl(url);
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current && isBlobPreviewUrl(previewUrlRef.current)) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (champignon) {
      form.reset({
        name: champignon.data?.name || '',
        attaque_f: champignon.data?.attaque_f || '',
        attaque_r: champignon.data?.attaque_r || '',
        category: champignon.data?.category,
      });

      if (champignon.data?.img_url) {
        const imgUrl = imageService.getOptimizedImageUrl(champignon.data.img_url.img1, 'champignon', 800, 600);
        if (imgUrl) {
          setPreview(imgUrl);
        }
      }
    }
  }, [champignon, form, setPreview]);

  const prepareImageFile = useCallback(async (file: File): Promise<File> => {
    setIsOptimizingImage(true);
    setCompressionMessage('Optimisation de l\'image en cours...');
    try {
      const result = await compressImageForUpload(file);
      if (result.wasCompressed) {
        setCompressionMessage(
          `Image optimisée : ${formatFileSize(result.originalSize)} → ${formatFileSize(result.compressedSize)}`,
        );
      } else {
        setCompressionMessage(null);
      }
      return result.file;
    } finally {
      setIsOptimizingImage(false);
    }
  }, []);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const validationError = validateImageFile(file);
    if (validationError) {
      setError(validationError);
      setImageFile(null);
      e.target.value = '';
      return;
    }
    setError(null);
    try {
      const optimizedFile = await prepareImageFile(file);
      setImageFile(optimizedFile);
      setPreview(URL.createObjectURL(optimizedFile));
    } catch (optimizationError) {
      console.error('Image optimization failed:', optimizationError);
      setError(resolveUploadErrorMessage(optimizationError));
      setImageFile(null);
      e.target.value = '';
    }
  };

  const uploadChampignonImage = useCallback(async (champignonId: number, file: File) => {
    setIsUploading(true);
    setUploadProgress(0);
    try {
      await uploadImage.mutateAsync({
        id: champignonId,
        imageFile: file,
        onProgress: setUploadProgress,
      });
      setPendingUploadId(null);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [uploadImage]);

  const retryUpload = useCallback(async () => {
    if (!pendingUploadId || !imageFile) {
      return;
    }
    setError(null);
    try {
      await uploadChampignonImage(pendingUploadId, imageFile);
      onSuccess();
    } catch (uploadError) {
      setError(resolveUploadErrorMessage(uploadError));
    }
  }, [imageFile, onSuccess, pendingUploadId, uploadChampignonImage]);

  const onSubmit = async (data: ChampignonsFormData) => {
    setIsSaving(true);
    setError(null);

    try {
      let createdId: number;

      if (isEditing && effectiveChampignonId) {
        await updateChampignon.mutateAsync({
          id: effectiveChampignonId,
          data: data as any
        });
        createdId = effectiveChampignonId;
      } else {
        const result = await createChampignon.mutateAsync(data);
        createdId = result.data.id;
      }

      if (imageFile && createdId) {
        try {
          await uploadChampignonImage(createdId, imageFile);
        } catch (uploadError) {
          setPendingUploadId(createdId);
          setError(resolveUploadErrorMessage(uploadError));
          return;
        }
      }

      onSuccess();
    } catch (submitError) {
      console.error('Error submitting form:', submitError);
      setError(resolveUploadErrorMessage(submitError));
    } finally {
      setIsSaving(false);
    }
  };

  const isSubmitting = isSaving || isUploading || isOptimizingImage;

  return {
    form,
    error,
    isEditing,
    isLoading,
    isSubmitting,
    isSaving,
    isUploading,
    isOptimizingImage,
    uploadProgress,
    compressionMessage,
    canRetryUpload: Boolean(pendingUploadId && imageFile),
    previewUrl,
    handleImageChange,
    onSubmit,
    retryUpload,
  };
}
